import { MaterialIcons } from '@expo/vector-icons';
import React from 'react';
import {
  Linking,
  Modal,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from 'react-native';

import { ThemedText } from '@/components/ThemedText';
import { env } from '@/config/env';
import AppStyles from '@/constants/Styles';
import { useThemeColor } from '@/hooks/useThemeColor';

interface AndroidAppPromptProps {
  visible: boolean;
  onDismiss: () => void;
}

/**
 * AndroidAppPrompt - Overlay component to encourage Android web users to download the native app
 * 
 * Features:
 * - Shows only on Android web browsers
 * - Displays for all authenticated Android web users
 * - Beautiful, non-intrusive design
 * - Links directly to Google Play Store
 * - Can be dismissed (will show again on next app open)
 */
export function AndroidAppPrompt({ visible, onDismiss }: AndroidAppPromptProps) {
  const colorScheme = useColorScheme();
  const cardBackground = useThemeColor({}, 'cardBackground');
  const textColor = useThemeColor({}, 'text');
  const tintColor = useThemeColor({}, 'tint');
  const iconColor = useThemeColor({}, 'icon');
  const backgroundColor = useThemeColor({}, 'background');
  const borderColor = useThemeColor({}, 'border');

  const handleDownload = () => {
    // Dismiss immediately for better UX
    onDismiss();
    
    // Open URL - use window.open on web for faster response, Linking on native
    if (Platform.OS === 'web') {
      window.open(env.androidAppDownloadUrl, '_blank');
    } else {
      // On native, try to open URL (no need to check canOpenURL first)
      Linking.openURL(env.androidAppDownloadUrl).catch(() => {
        // Silently handle errors opening Play Store
      });
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <TouchableOpacity
        style={[
          AppStyles.modalContainer,
          {
            backgroundColor: colorScheme === 'dark' 
              ? 'rgba(0, 0, 0, 0.85)' 
              : 'rgba(0, 0, 0, 0.5)'
          }
        ]}
        activeOpacity={1}
        onPress={onDismiss}
      >
        <TouchableOpacity
          style={[
            AppStyles.modalView,
            { 
              backgroundColor: colorScheme === 'dark' ? '#3a3a3c' : cardBackground,
              borderColor: borderColor,
              borderWidth: 1,
              shadowColor: backgroundColor,
              shadowOffset: {
                width: 0,
                height: colorScheme === 'dark' ? 8 : 4,
              },
              shadowOpacity: colorScheme === 'dark' ? 0.5 : 0.3,
              shadowRadius: colorScheme === 'dark' ? 16 : 8,
              elevation: colorScheme === 'dark' ? 16 : 8,
            }
          ]}
          activeOpacity={1}
          onPress={() => {}} // Prevent clicks inside the card from closing it
        >
          {/* Close button */}
          <TouchableOpacity
            style={AppStyles.modalCloseButton}
            onPress={onDismiss}
            accessibilityLabel="Close prompt"
            accessibilityHint="Dismisses the app download prompt"
          >
            <MaterialIcons name="close" size={24} color={iconColor} />
          </TouchableOpacity>

          {/* App Icon */}
          <View style={styles.iconContainer}>
            <View style={[
              styles.iconCircle, 
              { 
                backgroundColor: tintColor,
                shadowColor: backgroundColor,
                shadowOffset: {
                  width: 0,
                  height: 4,
                },
                shadowOpacity: colorScheme === 'dark' ? 0.4 : 0.2,
                shadowRadius: 8,
                elevation: 8,
              }
            ]}>
              <MaterialIcons name="download" size={48} color="white" />
            </View>
          </View>

          {/* Heading */}
          <ThemedText 
            style={[
              styles.heading,
              { color: textColor }
            ]}
          >
            Get the best experience with our Android app
          </ThemedText>

          {/* Download Button */}
          <TouchableOpacity
            style={[
              styles.downloadButton, 
              { 
                backgroundColor: tintColor,
                shadowColor: backgroundColor,
                shadowOffset: {
                  width: 0,
                  height: 2,
                },
                shadowOpacity: colorScheme === 'dark' ? 0.3 : 0.2,
                shadowRadius: 4,
                elevation: 4,
              }
            ]}
            onPress={handleDownload}
            activeOpacity={0.7}
            accessibilityLabel="Download app from Play Store"
            accessibilityHint="Opens Google Play Store to download the VotesNow app"
          >
            <MaterialIcons 
              name="get-app" 
              size={24} 
              color={colorScheme === 'dark' ? '#000' : 'white'} 
            />
            <ThemedText 
              style={[
                styles.downloadButtonText,
                { color: colorScheme === 'dark' ? '#000' : 'white' }
              ]}
            >
              Download Now
            </ThemedText>
          </TouchableOpacity>

          {/* Maybe Later Link */}
          <TouchableOpacity
            style={styles.maybeLaterButton}
            onPress={onDismiss}
            activeOpacity={0.7}
            accessibilityLabel="Maybe later"
            accessibilityHint="Dismisses the prompt for now"
          >
            <ThemedText style={[styles.maybeLaterText, { color: textColor }]}>
              Maybe Later
            </ThemedText>
          </TouchableOpacity>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
}

const styles = StyleSheet.create({
  iconContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  heading: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 24,
  },
  benefitsContainer: {
    marginBottom: 24,
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  benefitText: {
    fontSize: 16,
    marginLeft: 12,
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    marginBottom: 12,
  },
  downloadButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  maybeLaterButton: {
    paddingVertical: 8,
    alignItems: 'center',
  },
  maybeLaterText: {
    fontSize: 16,
    opacity: 0.7,
  },
});

